import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import NewsCard from "@/components/news/NewsCard";
import ShareButtons from "@/components/news/ShareButtons";
import { formatDateWithWeekday, formatTime, formatDateLong } from "@/lib/date";
import { TID } from "@/lib/testIds";
import { Clock, User, Tag as TagIcon } from "lucide-react";
import AdSlot from "@/components/ads/AdSlot";
import Sidebar from "@/components/ads/Sidebar";
import { useSEO } from "@/lib/useSEO";
import { useDevice } from "@/lib/useDevice";

export default function ArticlePage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const device = useDevice();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [seo, setSeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const trackedRef = useRef(null); // guards React StrictMode double-invoke

  useEffect(() => {
    setLoading(true);
    setError(null);
    setArticle(null);
    setRelated([]);
    setSeo(null);
    api
      .article(slug)
      .then((a) => {
        setArticle(a);
        // Track view once per slug per mount (server also dedupes per session for 30 min)
        if (trackedRef.current !== slug) {
          trackedRef.current = slug;
          api.trackArticleView(slug, device);
        }
        // Load /related + /seo independently — a failure in either MUST NOT
        // convert the fully-loaded article into a "Noticia no encontrada".
        return Promise.all([
          api.related(slug, 4).catch(() => []),
          api.articleSEO(slug).catch(() => null),
        ]);
      })
      .then(([r, s]) => {
        setRelated(r || []);
        setSeo(s || null);
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [slug]);

  useSEO(seo ? {
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonical: seo.canonical,
    image: seo.image,
    og: seo.og,
    twitter: seo.twitter,
    facebookAppId: seo.facebook_app_id,
    jsonLd: seo.json_ld,
  } : {});

  if (loading) {
    return (
      <div className="container-narrow py-10">
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-24 bg-softblue/70" />
          <div className="h-10 w-3/4 bg-softblue/60" />
          <div className="h-6 w-full bg-softblue/50" />
          <div className="aspect-[16/9] bg-softblue/50 mt-4" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="container-narrow py-16 text-center">
        <p className="text-textgray text-sm uppercase tracking-widest">Noticia no encontrada</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 inline-block text-petroleum hover:text-turquoise font-semibold uppercase text-xs tracking-widest"
        >
          Volver a la portada
        </button>
      </div>
    );
  }

  return (
    <article data-testid={TID.articlePage} className="pb-16">
      {/* Header block */}
      <div className="container-narrow pt-8 pb-6">
        <Link
          to={`/categoria/${article.main_category_slug}`}
          className="kicker inline-block"
        >
          {article.main_category_name}
        </Link>
        <h1
          data-testid={TID.articleTitle}
          className="mt-3 font-serif font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-blueblack text-balance"
        >
          {article.title}
        </h1>
        {article.summary && (
          <p data-testid={TID.articleSummary} className="mt-4 text-lg sm:text-xl text-textgray leading-relaxed font-light">
            {article.summary}
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] uppercase tracking-[0.14em] text-textgray">
          <span data-testid={TID.articleDate} className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {formatDateWithWeekday(article.published_at)} · {formatTime(article.published_at)} hs
          </span>
          {article.updated_at && article.updated_at !== article.published_at && (
            <span className="text-textgray/80">Actualizado: {formatDateLong(article.updated_at)}</span>
          )}
        </div>
      </div>

      {/* Main image */}
      <figure className="container-wide mb-8">
        <div className="aspect-[16/9] bg-lightgray overflow-hidden">
          <img
            src={article.main_image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        {(article.image_caption || article.image_credit) && (
          <figcaption className="container-narrow !px-0 mt-2 text-xs text-textgray flex flex-col sm:flex-row gap-1 sm:gap-4">
            {article.image_caption && <span>{article.image_caption}</span>}
            {article.image_credit && (
              <span className="uppercase tracking-widest text-textgray/80">Foto: {article.image_credit}</span>
            )}
          </figcaption>
        )}
      </figure>

      {/* Content */}
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8 items-start">
          <div className="flex-1 min-w-0">
            <div className="mb-6">
              <ShareButtons url={location.pathname} title={seo?.title || article.title} canonicalUrl={seo?.canonical} description={seo?.description || article.summary} />
            </div>
            <div
              data-testid={TID.articleContent}
              className="article-body"
              dangerouslySetInnerHTML={{ __html: article.content_html || article.content || "" }}
            />

            <AdSlot
              slot="in-article"
              category={article.main_category_slug}
              articleId={article.id}
              className="my-8 flex justify-center"
            />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mt-10 pt-6 border-t border-softblue flex flex-wrap items-center gap-2">
                <TagIcon className="w-4 h-4 text-textgray" />
                {article.tags.map((t) => (
                  <span key={t} className="inline-block text-xs uppercase tracking-widest bg-lightgray text-nightblue px-3 py-1 border border-softblue">
                    {t}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-8">
              <ShareButtons url={location.pathname} title={seo?.title || article.title} canonicalUrl={seo?.canonical} description={seo?.description || article.summary} />
            </div>
          </div>
          <Sidebar category={article.main_category_slug} articleId={article.id} />
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section data-testid={TID.relatedList} className="container-wide mt-16">
          <AdSlot
            slot="before-related"
            category={article.main_category_slug}
            articleId={article.id}
            className="mb-8 flex justify-center"
          />
          <h2 className="section-title mb-6">
            <span className="section-title-bar" />
            Noticias relacionadas
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((a) => (
              <NewsCard key={a.id} article={a} variant="default" />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
